/* ============================================================================
   Rose Gold Victory — Cart Drawer (Phase 1)
   Owner: design-director
   Vanilla JS, no jQuery dependency.

   Responsibilities (Phase 1):
     - Intercept form[action*="/cart/add"] submissions site-wide via a
       capture-phase listener (wins over jQuery bubble-phase handlers in
       templates/product.liquid and templates/product.quick_view.liquid).
     - POST to /cart/add.js, then GET /cart.js, render line items, open
       drawer.
     - Hijack header cart link clicks (a[href*="/cart"].header-cart) to
       open the drawer instead of navigating.
     - Close on backdrop click / close button / Esc key.
     - Restore focus to the trigger element on close.

   Risk discipline:
     - Capture phase + stopImmediatePropagation prevents the legacy
       CartJS handler in snippets/theme-modals.liquid from double-POSTing
       when settings.ajax_cart === true (default).
     - Fail-open: if /cart/add.js fetch fails, set a bypass attribute on
       the form and call form.submit() so the user is not stranded.
     - No client-side cart state divergence: every mutation re-fetches
       /cart.js.
     - Focus trap while open (Tab cycles within drawer).

   Also exposes RGVCartDrawer.addItem(id, qty, props) for grid ATC
   click handlers in theme.js that are not form submissions.

   Still deferred:
     - Qty +/- controls and remove (POST /cart/change.js)
     - Free-shipping threshold meter
     - Express payment buttons (Apple Pay / Shop Pay)
     - Swipe-to-dismiss on mobile
============================================================================ */

(function () {
  'use strict';

  // ---- Constants -------------------------------------------------------

  var SELECTORS = {
    drawer: '#CartDrawer',
    backdrop: '#CartDrawerBackdrop',
    closeBtn: '[data-rgv-cart-drawer-close]',
    items: '[data-rgv-cart-drawer-items]',
    empty: '[data-rgv-cart-drawer-empty]',
    footer: '[data-rgv-cart-drawer-footer]',
    subtotal: '[data-rgv-cart-drawer-subtotal]',
    countBadge: '[data-rgv-cart-drawer-count]',
    error: '[data-rgv-cart-drawer-error]',
    bootstrap: '[data-rgv-cart-drawer-bootstrap]',
    headerCart: 'a.header-cart',
    headerCartCount: '#CartCount',
    addForm: 'form[action*="/cart/add"]'
  };

  var STATE_OPEN = 'open';
  var BYPASS_ATTR = 'data-rgv-bypass';
  var BODY_LOCK_CLASS = 'is-rgv-cart-drawer-open';

  // ---- Lifecycle -------------------------------------------------------

  function init() {
    var drawer = document.querySelector(SELECTORS.drawer);
    var backdrop = document.querySelector(SELECTORS.backdrop);
    if (!drawer || !backdrop) return;

    // Hydrate from server-rendered bootstrap so the count badge etc.
    // is already accurate before any user interaction.
    var bootstrap = readBootstrap(drawer);

    bindAddToCartIntercept();
    bindHeaderCartHijack(drawer);
    bindCloseInteractions(drawer, backdrop);

    // Expose a tiny API for other scripts (and for QA in dev tools).
    window.RGVCartDrawer = {
      open: function (trigger) { open(drawer, backdrop, trigger || null); },
      close: function () { close(drawer, backdrop); },
      refresh: function () { return refresh(drawer); },
      addItem: function (id, qty, props) {
        return addItemById(drawer, backdrop, id, qty, props || {});
      }
    };

    // Initial bootstrap hydration (count badge already correct from
    // server render; this just normalizes if the JSON differs).
    if (bootstrap) {
      updateBadges(bootstrap);
    }
  }

  function readBootstrap(drawer) {
    var node = drawer.parentNode
      ? drawer.parentNode.querySelector(SELECTORS.bootstrap)
      : null;
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  // ---- Add-to-cart intercept ------------------------------------------

  function bindAddToCartIntercept() {
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || form.tagName !== 'FORM') return;

        // Only intercept add-to-cart forms.
        var action = form.getAttribute('action') || '';
        if (action.indexOf('/cart/add') === -1) return;

        // If we marked this form for bypass (fail-open path), let the
        // browser submit it natively.
        if (form.hasAttribute(BYPASS_ATTR)) return;

        // Block the legacy CartJS bubble-phase handler from also POSTing.
        event.preventDefault();
        event.stopImmediatePropagation();

        handleAddToCart(form);
      },
      true /* useCapture: wins over jQuery .submit() handlers */
    );
  }

  function handleAddToCart(form) {
    var drawer = document.querySelector(SELECTORS.drawer);
    var backdrop = document.querySelector(SELECTORS.backdrop);
    if (!drawer || !backdrop) {
      // Drawer missing — fail-open
      submitFallback(form);
      return;
    }

    clearError(drawer);
    setBusy(form, true);

    var formData = new FormData(form);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (resp) {
        return resp.json().then(function (data) {
          return { ok: resp.ok, status: resp.status, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          // Shopify returns a JSON error envelope — surface it in-drawer.
          var msg = (result.data && (result.data.description || result.data.message)) ||
            'Could not add to cart.';
          showError(drawer, msg);
          // Open drawer so the user sees the error context.
          return refresh(drawer).then(function () {
            open(drawer, backdrop, form);
          });
        }
        return refresh(drawer).then(function () {
          open(drawer, backdrop, form);
        });
      })
      .catch(function () {
        // Network or unexpected — fail-open: let the form submit natively.
        submitFallback(form);
      })
      .then(function () {
        setBusy(form, false);
      });
  }

  function addItemById(drawer, backdrop, id, qty, props) {
    clearError(drawer);
    var body = { id: id, quantity: qty || 1 };
    if (props && typeof props === 'object') {
      body.properties = props;
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(function (resp) {
        return resp.json().then(function (data) {
          return { ok: resp.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          var msg =
            (result.data && (result.data.description || result.data.message)) ||
            'Could not add to cart.';
          showError(drawer, msg);
          return refresh(drawer).then(function () {
            open(drawer, backdrop, null);
            throw new Error(msg);
          });
        }
        return refresh(drawer).then(function () {
          open(drawer, backdrop, null);
        });
      });
  }

  function submitFallback(form) {
    try {
      form.setAttribute(BYPASS_ATTR, 'true');
      // Programmatic submit() does NOT fire a 'submit' event on the form,
      // so our capture-phase listener will not re-intercept. The bypass
      // attribute is belt-and-braces.
      form.submit();
    } catch (e) {
      // Last resort: nothing more we can do.
    }
  }

  function setBusy(form, busy) {
    var btn = form.querySelector('[type="submit"], button:not([type])');
    if (btn) {
      if (busy) {
        btn.setAttribute('aria-busy', 'true');
        btn.dataset.rgvOriginalDisabled = btn.disabled ? '1' : '0';
        btn.disabled = true;
      } else {
        btn.removeAttribute('aria-busy');
        btn.disabled = btn.dataset.rgvOriginalDisabled === '1';
        delete btn.dataset.rgvOriginalDisabled;
      }
    }
  }

  // ---- Header cart icon hijack ----------------------------------------

  function bindHeaderCartHijack(drawer) {
    // Capture phase + stopImmediatePropagation — legacy theme.min.js
    // also binds a click handler on `.header-cart` (verified via grep);
    // without stopping propagation, both the legacy mini-cart drawer
    // and the new drawer would open simultaneously.
    document.addEventListener('click', function (event) {
      // Respect modifier keys / non-primary buttons (new tab, middle-click).
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (typeof event.button === 'number' && event.button !== 0) return;

      // Use closest() with a guard for non-element targets (e.g. <i>).
      var target = event.target.closest ? event.target.closest(SELECTORS.headerCart) : null;
      if (!target) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      var backdrop = document.querySelector(SELECTORS.backdrop);
      if (!backdrop) return;
      open(drawer, backdrop, target);
    }, true /* useCapture */);
  }

  // ---- Close interactions ---------------------------------------------

  function bindCloseInteractions(drawer, backdrop) {
    // Backdrop click
    backdrop.addEventListener('click', function () {
      close(drawer, backdrop);
    });

    // Close button (delegated within drawer)
    drawer.addEventListener('click', function (event) {
      var btn = event.target.closest ? event.target.closest(SELECTORS.closeBtn) : null;
      if (btn) close(drawer, backdrop);
    });

    // Esc key
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawer.getAttribute('data-rgv-cart-drawer-state') === STATE_OPEN) {
        close(drawer, backdrop);
      }
    });
  }

  // ---- Open / close ----------------------------------------------------

  var lastTrigger = null;
  var focusTrapHandler = null;

  var FOCUSABLE =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

  function getFocusable(drawer) {
    return Array.prototype.slice
      .call(drawer.querySelectorAll(FOCUSABLE))
      .filter(function (el) {
        return !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true';
      });
  }

  function bindFocusTrap(drawer) {
    unbindFocusTrap();
    focusTrapHandler = function (event) {
      if (event.key !== 'Tab') return;
      if (drawer.getAttribute('data-rgv-cart-drawer-state') !== STATE_OPEN) return;
      var nodes = getFocusable(drawer);
      if (!nodes.length) {
        event.preventDefault();
        return;
      }
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', focusTrapHandler, true);
  }

  function unbindFocusTrap() {
    if (focusTrapHandler) {
      document.removeEventListener('keydown', focusTrapHandler, true);
      focusTrapHandler = null;
    }
  }

  function open(drawer, backdrop, trigger) {
    if (drawer.getAttribute('data-rgv-cart-drawer-state') === STATE_OPEN) return;

    lastTrigger = trigger || null;

    // Make the elements participate in layout BEFORE the transition;
    // hidden -> not-hidden, then on the next frame add the open state
    // so the transform actually animates.
    drawer.removeAttribute('hidden');
    backdrop.removeAttribute('hidden');
    drawer.setAttribute('aria-hidden', 'false');
    backdrop.setAttribute('aria-hidden', 'true'); // backdrop is decorative

    // Body scroll lock.
    document.documentElement.classList.add(BODY_LOCK_CLASS);

    // Force layout flush, then add the open state on next frame.
    // requestAnimationFrame double-tick is the most reliable approach.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        drawer.setAttribute('data-rgv-cart-drawer-state', STATE_OPEN);
        backdrop.setAttribute('data-rgv-cart-drawer-state', STATE_OPEN);
      });
    });

    bindFocusTrap(drawer);

    // Initial focus on close button.
    var closeBtn = drawer.querySelector(SELECTORS.closeBtn);
    if (closeBtn) {
      // Small delay so the focus shift happens after the panel is visible.
      setTimeout(function () { closeBtn.focus(); }, 50);
    }
  }

  function close(drawer, backdrop) {
    if (drawer.getAttribute('data-rgv-cart-drawer-state') !== STATE_OPEN) return;

    drawer.removeAttribute('data-rgv-cart-drawer-state');
    backdrop.removeAttribute('data-rgv-cart-drawer-state');
    drawer.setAttribute('aria-hidden', 'true');

    unbindFocusTrap();
    document.documentElement.classList.remove(BODY_LOCK_CLASS);

    // After the transition ends, set hidden so the element is fully
    // out of the accessibility tree. Use a one-shot listener.
    var onEnd = function () {
      drawer.removeEventListener('transitionend', onEnd);
      if (drawer.getAttribute('data-rgv-cart-drawer-state') !== STATE_OPEN) {
        drawer.setAttribute('hidden', '');
        backdrop.setAttribute('hidden', '');
      }
    };
    drawer.addEventListener('transitionend', onEnd);
    // Fallback: if transitionend doesn't fire (reduced motion, browser
    // quirk), force hidden after the longest token duration + buffer.
    setTimeout(onEnd, 600);

    // Restore focus to the trigger.
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      try { lastTrigger.focus(); } catch (e) { /* noop */ }
    }
    lastTrigger = null;
  }

  // ---- Refresh (re-render from /cart.js) ------------------------------

  function refresh(drawer) {
    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (resp) { return resp.json(); })
      .then(function (cart) {
        render(drawer, cart);
        updateBadges(cart);
        // Cart-change signal for non-drawer consumers (free-shipping
        // message in theme.js). Replaces the legacy CartJS
        // 'cart.requestComplete' jQuery event.
        try {
          document.dispatchEvent(new CustomEvent('rgv:cart:updated', { detail: { cart: cart } }));
        } catch (e) { /* CustomEvent unavailable — non-critical signal */ }
        return cart;
      })
      .catch(function () {
        // Render failure shouldn't break the page; just leave previous state.
      });
  }

  // ---- Render ----------------------------------------------------------

  function render(drawer, cart) {
    var itemsList = drawer.querySelector(SELECTORS.items);
    var emptyMsg = drawer.querySelector(SELECTORS.empty);
    var footer = drawer.querySelector(SELECTORS.footer);
    var subtotalEl = drawer.querySelector(SELECTORS.subtotal);
    var countBadge = drawer.querySelector(SELECTORS.countBadge);

    if (!itemsList) return;

    if (!cart.items || cart.items.length === 0) {
      itemsList.innerHTML = '';
      itemsList.setAttribute('hidden', '');
      if (emptyMsg) emptyMsg.removeAttribute('hidden');
      if (footer) footer.setAttribute('hidden', '');
    } else {
      itemsList.innerHTML = cart.items.map(renderLineItem).join('');
      itemsList.removeAttribute('hidden');
      if (emptyMsg) emptyMsg.setAttribute('hidden', '');
      if (footer) footer.removeAttribute('hidden');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    }

    if (countBadge) countBadge.textContent = String(cart.item_count || 0);
  }

  function renderLineItem(item) {
    // Mirrors snippets/cart-drawer-line-item.liquid. Keep in sync.
    var imageHtml;
    if (item.image) {
      imageHtml =
        '<img class="rgv-cart-drawer__item-image" ' +
        'src="' + sizedImage(item.image, 120) + '" ' +
        'alt="" width="60" height="60" loading="lazy">';
    } else {
      imageHtml =
        '<span class="rgv-cart-drawer__item-image rgv-cart-drawer__item-image--placeholder" aria-hidden="true"></span>';
    }

    var variantHtml = '';
    if (item.variant_title && item.variant_title !== 'Default Title') {
      variantHtml =
        '<p class="rgv-cart-drawer__item-variant">' +
        escapeHtml(item.variant_title) +
        '</p>';
    }

    // Line-item properties (e.g. the PDP gift message). Skip Shopify's
    // private underscore-prefixed keys and empty values — mirrors the
    // cart page's own properties loop in sections/cart-template.liquid.
    var propsHtml = '';
    if (item.properties && typeof item.properties === 'object') {
      Object.keys(item.properties).forEach(function (k) {
        var v = item.properties[k];
        if (!v || k.charAt(0) === '_') return;
        propsHtml +=
          '<p class="rgv-cart-drawer__item-prop">' +
          escapeHtml(k) + ': ' + escapeHtml(String(v)) +
          '</p>';
      });
    }

    var url = item.url || '#';

    return (
      '<li class="rgv-cart-drawer__item" data-rgv-cart-drawer-line data-line-key="' +
        escapeAttr(item.key || '') + '">' +
        '<a class="rgv-cart-drawer__item-image-link" href="' + escapeAttr(url) +
          '" tabindex="-1" aria-hidden="true">' +
          imageHtml +
        '</a>' +
        '<div class="rgv-cart-drawer__item-details">' +
          '<a class="rgv-cart-drawer__item-title" href="' + escapeAttr(url) + '">' +
            escapeHtml(item.product_title || item.title || '') +
          '</a>' +
          variantHtml +
          propsHtml +
          '<p class="rgv-cart-drawer__item-meta">' +
            '<span class="rgv-cart-drawer__item-qty">Qty: ' +
              String(item.quantity || 1) +
            '</span>' +
            '<span class="rgv-cart-drawer__item-price">' +
              formatMoney(item.line_price || item.final_line_price || 0) +
            '</span>' +
          '</p>' +
        '</div>' +
      '</li>'
    );
  }

  function updateBadges(cart) {
    var count = cart && cart.item_count != null ? cart.item_count : 0;
    var headerCount = document.querySelector(SELECTORS.headerCartCount);
    if (headerCount) headerCount.textContent = String(count);

    // Keep header cart link accessible name in sync with the badge
    // (audit R6 — static aria-label would otherwise stale after ATC).
    updateHeaderCartLabel(count);

    var drawerCount = document.querySelector(SELECTORS.countBadge);
    if (drawerCount) drawerCount.textContent = String(count);
  }

  function updateHeaderCartLabel(count) {
    var link = document.querySelector(SELECTORS.headerCart);
    if (!link) return;
    var title = link.getAttribute('data-cart-title') || 'Cart';
    var one = link.getAttribute('data-cart-item-one') || 'item';
    var other = link.getAttribute('data-cart-item-other') || 'items';
    var n = Number(count) || 0;
    var unit = n === 1 ? one : other;
    link.setAttribute('aria-label', title + ', ' + n + ' ' + unit);
  }

  function showError(drawer, message) {
    var el = drawer.querySelector(SELECTORS.error);
    if (!el) return;
    el.textContent = message;
    el.removeAttribute('hidden');
  }

  function clearError(drawer) {
    var el = drawer.querySelector(SELECTORS.error);
    if (!el) return;
    el.textContent = '';
    el.setAttribute('hidden', '');
  }

  // ---- Money formatting ------------------------------------------------
  // Local Shopify money_format renderer. Avoids dependency on
  // Shopify.formatMoney (which only loads on /account/ pages via
  // shopify_common.js — see layout/theme.liquid:179).
  // Supports: {{amount}}, {{amount_no_decimals}},
  //   {{amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}},
  //   {{amount_with_space_separator}}, {{amount_no_decimals_with_space_separator}}

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var amount = (parseInt(cents, 10) || 0) / 100;
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount':
          return formatNumber(amount, 2, ',', '.');
        case 'amount_no_decimals':
          return formatNumber(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return formatNumber(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return formatNumber(amount, 0, '.', ',');
        case 'amount_with_space_separator':
          return formatNumber(amount, 2, ' ', '.');
        case 'amount_no_decimals_with_space_separator':
          return formatNumber(amount, 0, ' ', ',');
        default:
          return formatNumber(amount, 2, ',', '.');
      }
    });
  }

  function formatNumber(amount, decimals, thousands, decimal) {
    var fixed = amount.toFixed(decimals);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  }

  // ---- HTML / attr escaping -------------------------------------------

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Inserts a Shopify image-size suffix (e.g. _120x120) into the URL
  // before the file extension. Falls back to the original URL if no
  // extension is found. /cart.js returns CDN URLs without a size suffix.
  function sizedImage(url, size) {
    if (!url) return '';
    var dim = String(size) + 'x' + String(size);
    return String(url).replace(
      /(\.(?:jpg|jpeg|png|gif|webp))(\?.*)?$/i,
      function (_, ext, query) {
        return '_' + dim + ext + (query || '');
      }
    );
  }

  // ---- Boot ------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
